import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import AskAIWidget from './components/AskAIWidget';
import RevenueChart from './components/RevenueChart';
import JobsStatusWidget from './components/JobsStatusWidget';
import LeadSourcesWidget from './components/LeadSourcesWidget';
import RecentJobsFeed from './components/RecentJobsFeed';
import UpcomingSchedule from './components/UpcomingSchedule';
import TeamOverviewWidget from './components/TeamOverviewWidget';
import UsageDashboardWidget from './components/UsageDashboardWidget';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, sub, icon, gradient, loading, onClick }) => (
  <motion.div
    whileHover={{ y: -2, scale: 1.01 }}
    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    onClick={onClick}
    className={`relative overflow-hidden rounded-2xl p-5 cursor-pointer shadow-sm border border-white/10 ${gradient}`}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-white/70 mb-1 uppercase tracking-wide">{title}</p>
        {loading ? (
          <div className="h-8 w-20 bg-white/20 animate-pulse rounded-lg mb-1" />
        ) : (
          <p className="text-3xl font-bold text-white leading-none mb-1">{value}</p>
        )}
        {sub && <p className="text-xs text-white/60">{sub}</p>}
      </div>
      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
        <Icon name={icon} size={20} color="white" />
      </div>
    </div>
    {/* Decorative circle */}
    <div className="absolute -bottom-4 -right-4 w-20 h-20 rounded-full bg-white/5" />
  </motion.div>
);

// ─── Quick Action Button ──────────────────────────────────────────────────────
const QuickAction = ({ icon, label, path, color }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(path)}
      className={`flex flex-col items-center justify-center gap-2 p-3 rounded-xl transition-all hover:scale-105 active:scale-95 ${color}`}
    >
      <Icon name={icon} size={18} />
      <span className="text-xs font-medium leading-tight text-center">{label}</span>
    </button>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { profile, organization } = useAuth();
  const navigate = useNavigate();

  const [metrics, setMetrics] = useState({
    revenueMonth: 0,
    jobsMonth: 0,
    leadsMonth: 0,
    completedJobs: 0,
    openDeals: 0,
    activitiesOverdue: 0,
    conversionRate: 0,
    avgJobValue: 0,
  });
  const [loading, setLoading] = useState(true);

  const handleMenuToggle = () => setIsSidebarOpen(!isSidebarOpen);
  const handleSidebarClose = () => setIsSidebarOpen(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e?.key === 'Escape') setIsSidebarOpen(false); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (!profile?.org_id) return;
    const fetchMetrics = async () => {
      setLoading(true);
      try {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString();

        const [
          jobsMonthRes,
          completedJobsRes,
          leadsMonthRes,
          allLeadsRes,
          openDealsRes,
          overdueRes,
        ] = await Promise.all([
          supabase?.from('jobs')?.select('total_cost, value, status')?.eq('org_id', profile?.org_id)?.is('deleted_at', null)?.gte('created_at', firstOfMonth),
          supabase?.from('jobs')?.select('id', { count: 'exact', head: true })?.eq('org_id', profile?.org_id)?.is('deleted_at', null)?.eq('status', 'completed'),
          supabase?.from('leads')?.select('id', { count: 'exact', head: true })?.eq('org_id', profile?.org_id)?.is('deleted_at', null)?.gte('created_at', firstOfMonth),
          supabase?.from('leads')?.select('status')?.eq('org_id', profile?.org_id)?.is('deleted_at', null),
          supabase?.from('deals')?.select('id', { count: 'exact', head: true })?.eq('org_id', profile?.org_id)?.is('deleted_at', null),
          supabase?.from('activities')?.select('id', { count: 'exact', head: true })?.eq('org_id', profile?.org_id)?.eq('status', 'overdue'),
        ]);

        const monthJobs = jobsMonthRes?.data || [];
        const revenueMonth = monthJobs?.reduce((s, j) => s + (Number(j?.total_cost) || Number(j?.value) || 0), 0);
        const completedMonthJobs = monthJobs?.filter(j => j?.status === 'completed');
        const avgJobValue = completedMonthJobs?.length > 0
          ? Math.round(completedMonthJobs?.reduce((s, j) => s + (Number(j?.total_cost) || Number(j?.value) || 0), 0) / completedMonthJobs?.length)
          : 0;

        const allLeads = allLeadsRes?.data || [];
        const convertedLeads = allLeads?.filter(l => l?.status === 'converted')?.length;
        const conversionRate = allLeads?.length > 0 ? Math.round((convertedLeads / allLeads?.length) * 100) : 0;

        setMetrics({
          revenueMonth,
          jobsMonth: monthJobs?.length,
          leadsMonth: leadsMonthRes?.count || 0,
          completedJobs: completedJobsRes?.count || 0,
          openDeals: openDealsRes?.count || 0,
          activitiesOverdue: overdueRes?.count || 0,
          conversionRate,
          avgJobValue,
        });
      } catch (err) {
        console.error('Dashboard metrics error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();

    // Real-time updates
    const channel = supabase?.channel('dashboard_metrics')?.on('postgres_changes', { event: '*', schema: 'public', table: 'jobs', filter: `org_id=eq.${profile?.org_id}` }, fetchMetrics)?.on('postgres_changes', { event: '*', schema: 'public', table: 'leads', filter: `org_id=eq.${profile?.org_id}` }, fetchMetrics)?.on('postgres_changes', { event: '*', schema: 'public', table: 'deals', filter: `org_id=eq.${profile?.org_id}` }, fetchMetrics)?.subscribe();

    return () => supabase?.removeChannel(channel);
  }, [profile?.org_id]);

  const firstName = profile?.full_name?.split(' ')?.[0] || 'there';
  const orgName = organization?.name || '';

  const statCards = [
    {
      title: 'Revenue This Month',
      value: `$${metrics?.revenueMonth >= 1000 ? (metrics?.revenueMonth / 1000)?.toFixed(1) + 'k' : metrics?.revenueMonth?.toLocaleString()}`,
      sub: `Avg $${metrics?.avgJobValue?.toLocaleString()} per job`,
      icon: 'DollarSign',
      gradient: 'bg-gradient-to-br from-emerald-500 to-teal-600',
      onClick: () => navigate('/reports'),
    },
    {
      title: 'Jobs This Month',
      value: String(metrics?.jobsMonth),
      sub: `${metrics?.completedJobs} completed all-time`,
      icon: 'Briefcase',
      gradient: 'bg-gradient-to-br from-blue-500 to-indigo-600',
      onClick: () => navigate('/jobs'),
    },
    {
      title: 'New Leads',
      value: String(metrics?.leadsMonth),
      sub: `${metrics?.conversionRate}% conversion rate`,
      icon: 'UserPlus',
      gradient: 'bg-gradient-to-br from-violet-500 to-purple-600',
      onClick: () => navigate('/leads'),
    },
    {
      title: 'Open Deals',
      value: String(metrics?.openDeals),
      sub: metrics?.activitiesOverdue > 0 ? `${metrics?.activitiesOverdue} overdue tasks` : 'Pipeline active',
      icon: 'Target',
      gradient: metrics?.activitiesOverdue > 0
        ? 'bg-gradient-to-br from-orange-500 to-red-500' :'bg-gradient-to-br from-amber-500 to-orange-500',
      onClick: () => navigate('/pipeline'),
    },
  ];

  const quickActions = [
    { label: 'New Lead', icon: 'UserPlus', path: '/leads', color: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 hover:bg-violet-200 dark:hover:bg-violet-900/50' },
    { label: 'New Job', icon: 'Briefcase', path: '/jobs', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50' },
    { label: 'New Deal', icon: 'Target', path: '/deals', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50' },
    { label: 'Add Client', icon: 'Building2', path: '/accounts', color: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 hover:bg-teal-200 dark:hover:bg-teal-900/50' },
    { label: 'Calendar', icon: 'Calendar', path: '/calendar', color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50' },
    { label: 'Dispatch', icon: 'MapPin', path: '/dispatch', color: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 hover:bg-pink-200 dark:hover:bg-pink-900/50' },
    { label: 'Reports', icon: 'BarChart3', path: '/reports', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50' },
    { label: 'Pipeline', icon: 'GitBranch', path: '/pipeline', color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-900/50' },
  ];

  return (
    <>
      <Helmet>
        <title>Dashboard — {orgName || 'Wilcart CRM'}</title>
        <meta name="description" content="Business intelligence dashboard with real-time metrics, revenue, jobs, and leads." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleMenuToggle} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />

        <main className="lg:ml-64 mt-24">
          <div className="p-4 lg:p-6 max-w-[1600px]">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              {/* ── Header ── */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                    Good {new Date()?.getHours() < 12 ? 'morning' : new Date()?.getHours() < 17 ? 'afternoon' : 'evening'}, {firstName} 👋
                  </h1>
                  <p className="text-muted-foreground text-sm mt-1">
                    {orgName && <span className="font-medium text-foreground">{orgName} · </span>}
                    {new Date()?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <button
                  onClick={() => navigate('/reports')}
                  className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm"
                >
                  <Icon name="BarChart3" size={15} />
                  Full Reports
                </button>
              </div>

              {/* ── Ask AI ── */}
              <div className="mb-4">
                <AskAIWidget orgId={profile?.org_id} />
              </div>

              {/* ── KPI Stat Cards ── */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {statCards?.map((card, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: i * 0.06 }}
                  >
                    <StatCard {...card} loading={loading} />
                  </motion.div>
                ))}
              </div>

              {/* ── Quick Actions ── */}
              <div className="bg-card border border-border rounded-2xl p-4 mb-6 shadow-sm">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Quick Actions</p>
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                  {quickActions?.map((a, i) => (
                    <QuickAction key={i} {...a} />
                  ))}
                </div>
              </div>

              {/* ── Main Grid ── */}
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-5">
                {/* Revenue Chart — spans 2 cols */}
                <div className="xl:col-span-2">
                  <RevenueChart orgId={profile?.org_id} />
                </div>
                {/* Jobs Status */}
                <div>
                  <JobsStatusWidget orgId={profile?.org_id} />
                </div>
              </div>

              {/* ── Second Row ── */}
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-5">
                {/* Recent Jobs — spans 2 cols */}
                <div className="xl:col-span-2">
                  <RecentJobsFeed orgId={profile?.org_id} />
                </div>
                {/* Lead Sources */}
                <div>
                  <LeadSourcesWidget orgId={profile?.org_id} />
                </div>
              </div>

              {/* ── Third Row ── */}
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-5">
                {/* Upcoming Schedule — spans 2 cols */}
                <div className="xl:col-span-2">
                  <UpcomingSchedule orgId={profile?.org_id} />
                </div>
                {/* Team Overview */}
                <div>
                  <TeamOverviewWidget orgId={profile?.org_id} />
                </div>
              </div>

              {/* ── Usage Widget ── */}
              <UsageDashboardWidget />

            </motion.div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Dashboard;